import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import SEOHead from './SEOHead';
import NewTopBar from './NewTopBar';
import NewHeader from './NewHeader';
import NewFooter from './NewFooter';
import LegalText from './LegalText';

const CookiePolicy = () => {
  const { language } = useLanguage();
  const sl = language === 'sl';

  const content = sl ? {
    title: 'Politika piškotkov',
    metaDesc: 'Politika piškotkov spletne strani GENERAL LEDGER d.o.o. — računovodski servis, Rogatec, Slovenija.',
    lastUpdated: 'Zadnja posodobitev: januar 2026',
    intro: 'Ta politika pojasnjuje, kako spletna stran GENERAL LEDGER d.o.o. uporablja piškotke in podobne tehnologije.',
    sections: [
      {
        title: '1. Kaj so piškotki?',
        content: `Piškotki so majhne besedilne datoteke, ki jih spletna stran shrani na vašo napravo (računalnik, telefon, tablico), ko obiščete spletno stran.

Piškotki omogočajo, da spletna stran:
• Si zapomni vaše nastavitve (npr. izbrani jezik)
• Izboljša uporabniško izkušnjo
• Zbira statistične podatke o obiskanosti`
      },
      {
        title: '2. Vrste piškotkov, ki jih uporabljamo',
        content: `**Nujni piškotki**
Ti piškotki so potrebni za delovanje spletne strani in jih ni mogoče izklopiti. Vključujejo:
• Piškotke za shranjevanje soglasja za piškotke
• Piškotke za upravljanje seje

**Analitični piškotki (Google Analytics)**
Uporabljamo Google Analytics 4 za razumevanje, kako obiskovalci uporabljajo našo spletno stran:
• _ga — razlikuje unikatne uporabnike (veljavnost: 2 leti)
• _ga_* — vzdržuje stanje seje (veljavnost: 2 leti)

Te piškotke naložimo šele, ko nam date soglasje.

**Funkcijski piškotki**
Ti piškotki omogočajo izboljšano funkcionalnost:
• gl_language — shranjuje vaš izbrani jezik (veljavnost: 1 leto)
• gl_consent — shranjuje vaše soglasje za piškotke (veljavnost: 1 leto)`
      },
      {
        title: '3. Upravljanje piškotkov',
        content: `**Na naši strani**
Ob prvem obisku se prikaže obvestilo o piškotkih, kjer lahko:
• Sprejmete vse piškotke
• Sprejmete samo nujne piškotke

**V brskalniku**
Piškotke lahko upravljate tudi v nastavitvah brskalnika:
• Chrome: Nastavitve → Zasebnost in varnost → Piškotki
• Firefox: Nastavitve → Zasebnost in varnost
• Safari: Nastavitve → Zasebnost
• Edge: Nastavitve → Piškotki in dovoljenja za spletna mesta

Opomba: Blokiranje vseh piškotkov lahko vpliva na delovanje spletne strani.`
      },
      {
        title: '4. Piškotki tretjih oseb',
        content: `**Google Analytics**
Ponudnik: Google Ireland Limited
Namen: Analiza obiskanosti
Politika zasebnosti: https://policies.google.com/privacy

**Google reCAPTCHA** (če je omogočeno)
Ponudnik: Google Ireland Limited
Namen: Zaščita pred nezaželenimi sporočili
Politika zasebnosti: https://policies.google.com/privacy`
      },
      {
        title: '5. Pravna podlaga',
        content: `• **Nujni piškotki:** Uporabljamo jih na podlagi zakonitega interesa, saj so potrebni za delovanje spletne strani
• **Analitični in funkcijski piškotki:** Uporabljamo jih samo na podlagi vašega soglasja

Vaše soglasje lahko kadarkoli prekličete s klikom na "Nastavitve piškotkov" v nogi strani ali s čiščenjem piškotkov v brskalniku.`
      },
      {
        title: '6. Posodobitve politike',
        content: `To politiko lahko občasno posodobimo. O bistvenih spremembah vas bomo obvestili s prikazom novega obvestila o piškotkih.

Datum zadnje posodobitve je naveden na vrhu te strani.`
      },
      {
        title: '7. Kontakt',
        content: `Za vprašanja glede piškotkov nas lahko kontaktirate:

GENERAL LEDGER d.o.o.
E-pošta: info@general-ledger.eu
Telefon: +386 3 818 40 60`
      }
    ]
  } : {
    title: 'Cookie Policy',
    metaDesc: 'Cookie Policy for GENERAL LEDGER d.o.o. website — accounting firm, Rogatec, Slovenia.',
    lastUpdated: 'Last updated: January 2026',
    intro: 'This policy explains how the GENERAL LEDGER d.o.o. website uses cookies and similar technologies.',
    sections: [
      {
        title: '1. What are cookies?',
        content: `Cookies are small text files that a website stores on your device (computer, phone, tablet) when you visit the website.

Cookies allow the website to:
• Remember your settings (e.g. selected language)
• Improve user experience
• Collect statistical data about visits`
      },
      {
        title: '2. Types of cookies we use',
        content: `**Essential cookies**
These cookies are necessary for the website to function and cannot be disabled. They include:
• Cookies for storing cookie consent
• Session management cookies

**Analytics cookies (Google Analytics)**
We use Google Analytics 4 to understand how visitors use our website:
• _ga — distinguishes unique users (validity: 2 years)
• _ga_* — maintains session state (validity: 2 years)

We only load these cookies after you give consent.

**Functional cookies**
These cookies enable enhanced functionality:
• gl_language — stores your selected language (validity: 1 year)
• gl_consent — stores your cookie consent (validity: 1 year)`
      },
      {
        title: '3. Managing cookies',
        content: `**On our website**
On your first visit, a cookie notice will appear where you can:
• Accept all cookies
• Accept only essential cookies

**In your browser**
You can also manage cookies in your browser settings:
• Chrome: Settings → Privacy and security → Cookies
• Firefox: Settings → Privacy & Security
• Safari: Preferences → Privacy
• Edge: Settings → Cookies and site permissions

Note: Blocking all cookies may affect website functionality.`
      },
      {
        title: '4. Third-party cookies',
        content: `**Google Analytics**
Provider: Google Ireland Limited
Purpose: Traffic analysis
Privacy policy: https://policies.google.com/privacy

**Google reCAPTCHA** (if enabled)
Provider: Google Ireland Limited
Purpose: Protection against spam
Privacy policy: https://policies.google.com/privacy`
      },
      {
        title: '5. Legal basis',
        content: `• **Essential cookies:** We use them based on legitimate interest as they are necessary for the website to function
• **Analytics and functional cookies:** We use them only based on your consent

You can withdraw your consent at any time by clicking "Cookie Settings" in the footer or by clearing cookies in your browser.`
      },
      {
        title: '6. Policy updates',
        content: `We may update this policy from time to time. We will notify you of significant changes by displaying a new cookie notice.

The date of the last update is shown at the top of this page.`
      },
      {
        title: '7. Contact',
        content: `For questions about cookies, you can contact us:

GENERAL LEDGER d.o.o.
Email: info@general-ledger.eu
Phone: +386 3 818 40 60`
      }
    ]
  };

  return (
    <div className="min-h-screen bg-[#F6F2EA]">
      <SEOHead
        title={`${content.title} — General Ledger`}
        description={content.metaDesc}
        canonicalPath={sl ? "/piskotki" : "/cookies"}
        lang={sl ? 'sl' : 'en'}
        alternateUrl={sl ? '/cookies' : '/piskotki'}
      />
      <NewTopBar />
      <NewHeader />
      
      <main className="max-w-[900px] mx-auto px-6 py-16 lg:py-24">
        <div className="mb-12">
          <span className="text-[13px] font-bold tracking-[2.5px] text-[#7A5B28] block mb-4">
            {sl ? 'PRAVNE INFORMACIJE' : 'LEGAL INFORMATION'}
          </span>
          <h1 className="font-serif text-4xl lg:text-[56px] leading-[1.05] tracking-[-1px] text-[#15181C] mb-4">
            {content.title}
          </h1>
          <p className="text-[15px] text-[#7A5B28]">{content.lastUpdated}</p>
        </div>

        <div className="prose prose-lg max-w-none">
          <p className="text-[17px] leading-[1.7] text-[#4E5359] mb-10">
            {content.intro}
          </p>

          <hr className="border-[#E2DACB] my-10" />

          {content.sections.map((section) => (
            <section key={section.title} className="mb-10">
              <h2 className="font-serif text-2xl lg:text-[32px] text-[#15181C] mb-4">
                {section.title}
              </h2>
              <LegalText content={section.content} />
            </section>
          ))}
          
          {/* Additional SEO content */}
          <div className="border-t border-[#E2DACB] pt-8 mt-4">
            <p className="text-[14px] text-[#7E786D] leading-[1.8]">
              {sl 
                ? 'GENERAL LEDGER d.o.o. je računovodski servis s sedežem v Rogatcu, Slovenija. Uporabljamo piškotke za izboljšanje uporabniške izkušnje in analizo obiska. Za vprašanja o piškotkih nas kontaktirajte na info@general-ledger.eu. Naša spletna stran uporablja Google Analytics za razumevanje, kako obiskovalci uporabljajo strani. Vaše soglasje za piškotke lahko kadarkoli prekličete.'
                : 'GENERAL LEDGER d.o.o. is an accounting firm based in Rogatec, Slovenia. We use cookies to improve user experience and analyze site traffic. For questions about cookies, contact us at info@general-ledger.eu. Our website uses Google Analytics to understand how visitors use our pages. You can withdraw your cookie consent at any time.'}
            </p>
          </div>
        </div>
      </main>

      <NewFooter />
    </div>
  );
};

export default CookiePolicy;
